import { useEffect, useMemo, useRef, useState } from 'react';
import { createSelector } from '@reduxjs/toolkit';
import { useTypedSelector } from '../../../hooks/use-typed-selector';
import { IState } from '../../../interfaces/redux/IState';
import { IFiltersCustomerItem } from '../../../interfaces/redux/IFilters';
import { ITaskCount } from '../../../interfaces/redux/ITaskCounts';
import {
  IQsReportCountsRow,
  IQsReportFilters,
  IQsProductivityRecord,
  IQsReportHookResult,
  IServiceGroup,
  IQsReportResponse,
} from '../../../interfaces/report/IQsReport';
import { getTaskTypesInOrder } from '../../../services/utils/task-service';
import HTTP from '../../../services/http';

type LocalFilters = IQsReportFilters;
type CountsRow = IQsReportCountsRow;

const normalizeCustomerName = (s: string) => s.toString().trim().toUpperCase();

const selectCustomers: (state: IState) => IFiltersCustomerItem[] = state => state.filters.customers;
const selectTaskCountsData: (state: IState) => Record<string, ITaskCount> = state =>
  state.taskCounts?.data || {};

const selectCustomerUnits = createSelector(selectCustomers, customers => {
  const names = customers
    .map(c => normalizeCustomerName(c.short_name || c.name || ''))
    .filter(Boolean);
  return Array.from(new Set(names)).sort();
});

const selectTypesList = (state: IState): string[] =>
  getTaskTypesInOrder(selectTaskCountsData(state));

export function useQsReportData(): IQsReportHookResult {
  const [filters, setFilters] = useState<LocalFilters>({
    customerIds: [],
    serviceGroupIds: [],
    taskTypes: ['All'],
    fromDate: null,
    toDate: null,
  });

  const [apiData, setApiData] = useState<IQsProductivityRecord[]>([]);
  const [initialServiceGroups, setInitialServiceGroups] = useState<IServiceGroup[]>([]);
  const [hasFetchedInitialGroups, setHasFetchedInitialGroups] = useState<boolean>(false);
  const [lastRefreshTs, setLastRefreshTs] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const inflightKeyRef = useRef<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);
  const lastRequestKeyRef = useRef<string | null>(null);
  const lastRequestAtRef = useRef<number>(0);

  const customerUnits = useTypedSelector(selectCustomerUnits);
  const taskCounts = useTypedSelector(selectTaskCountsData);
  const typesList = useTypedSelector(selectTypesList);
  const customers = useTypedSelector(selectCustomers);

  const units = useMemo(() => {
    const selectedNames =
      filters.customerIds && filters.customerIds.length
        ? customers
            .filter(c => filters.customerIds.includes(c.id))
            .map(c => normalizeCustomerName(c.short_name || c.name || ''))
            .filter(Boolean)
        : customerUnits;
    return [...selectedNames, 'Total'];
  }, [customerUnits, filters.customerIds, customers]);

  const baseTypes = useMemo<string[]>(() => {
    return typesList.filter(t => t !== 'therapies' && t !== 'STAR' && t !== 'SMS' && t !== 'MR');
  }, [typesList]);

  const filteredTypes = useMemo<string[]>(() => {
    if (!filters.taskTypes.length || filters.taskTypes.includes('All')) return baseTypes;
    return baseTypes.filter(t => filters.taskTypes.includes(t));
  }, [baseTypes, filters.taskTypes]);

  useEffect(() => {
    const availableCustomerIds = customers.map(c => c.id).filter(Boolean);

    if (!availableCustomerIds.length) {
      setApiData([]);
      setLastRefreshTs(null);
      return;
    }

    const payloadCustomerIds =
      filters.customerIds && filters.customerIds.length > 0 ? filters.customerIds : [];
    const payloadServiceGroupIds =
      filters.serviceGroupIds && filters.serviceGroupIds.length > 0 ? filters.serviceGroupIds : [];

    const payloadKey = JSON.stringify({
      customerIds: payloadCustomerIds,
      serviceGroupIds: payloadServiceGroupIds,
    });
    if (loading && inflightKeyRef.current === payloadKey) {
      return;
    }
    const now = Date.now();
    if (lastRequestKeyRef.current === payloadKey && now - lastRequestAtRef.current < 3000) {
      return;
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;
    inflightKeyRef.current = payloadKey;
    setLoading(true);
    HTTP.post(
      '/reports/qs_productivity_per_task',
      {
        customerIds: payloadCustomerIds,
        serviceGroupIds: payloadServiceGroupIds,
      },
      { signal: controller.signal },
    )
      .then(res => {
        const data = (res?.data as IQsReportResponse) || {};
        const arr = data.qs_productivity_per_task || [];
        const apiGroups = data.service_groups || [];
        let groups: IServiceGroup[] = apiGroups;
        if ((!apiGroups || apiGroups.length === 0) && arr.length > 0) {
          const m = new Map<number, IServiceGroup>();
          arr.forEach(r => {
            const id = r.service_group_id ?? null;
            if (!id) return;
            const name = r.service_group_name || '';
            const display_name = r.service_group_display_name || '';
            if (!m.has(id)) m.set(id, { id, name, display_name });
          });
          groups = Array.from(m.values());
        }

        setApiData(arr);
        if (!hasFetchedInitialGroups && groups.length > 0) {
          setInitialServiceGroups(groups);
          setHasFetchedInitialGroups(true);
        }

        const maxTs = arr.reduce<string | null>((max, r) => {
          const ts = r.last_refresh_ts ?? null;
          if (!ts) return max;
          return !max || ts > max ? ts : max;
        }, null);
        setLastRefreshTs(maxTs);
      })
      .catch(() => {
        setApiData([]);
        setLastRefreshTs(null);
      })
      .finally(() => {
        setLoading(false);
        inflightKeyRef.current = null;
        lastRequestKeyRef.current = payloadKey;
        lastRequestAtRef.current = Date.now();
      });
  }, [customers, filters.customerIds, filters.serviceGroupIds, hasFetchedInitialGroups]);

  const rows = useMemo<CountsRow[]>(() => {
    return buildRowsFromApi(filteredTypes, apiData, units);
  }, [filteredTypes, apiData, units]);

  const taskTypes = useMemo<string[]>(() => ['All', ...baseTypes, 'Total'], [baseTypes]);

  const customerNames = useMemo<Record<string, string>>(() => {
    const map: Record<string, string> = {};
    customers.forEach(c => {
      const key = normalizeCustomerName(c.short_name || c.name || '');
      const val = c.name || c.short_name || '';
      if (key) map[key] = val;
    });
    return map;
  }, [customers]);

  const customerOptions = useMemo(() => {
    return customers.map(c => ({ id: c.id, label: c.name || c.short_name, short: c.short_name }));
  }, [customers]);

  const serviceGroupOptions = useMemo(() => {
    return (initialServiceGroups || [])
      .map(sg => ({
        id: sg.id,
        label: sg.display_name || sg.name || `Unknown (${sg.id})`,
      }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [initialServiceGroups]);

  return {
    filters,
    setFilters,
    units,
    taskTypes,
    rows,
    lastRefreshTs,
    loading,
    customerNames,
    customerOptions,
    serviceGroupOptions,
  };
}

export default useQsReportData;

export const buildRowsFromApi = (
  filteredTypes: string[],
  data: IQsProductivityRecord[],
  units: string[],
): CountsRow[] => {
  const baseRows = filteredTypes.map(taskType => {
    const countsPerCustomer = units.reduce((acc, unit) => {
      if (unit === 'Total') return acc;
      const countForCustomer = data
        .filter(
          r => r.task_name === taskType && normalizeCustomerName(r.customer_short_name) === unit,
        )
        .reduce((sum, r) => sum + (r.row_count || 0), 0);
      acc[unit] = countForCustomer;
      return acc;
    }, {} as Record<string, number>);

    const totalForType = Object.keys(countsPerCustomer).reduce(
      (sum, key) => sum + (countsPerCustomer[key] || 0),
      0,
    );

    return { taskType, counts: { ...countsPerCustomer, Total: totalForType } };
  });

  const totalRowCounts = units.reduce((acc, unit) => {
    if (unit === 'Total') return acc;
    acc[unit] = data
      .filter(r => normalizeCustomerName(r.customer_short_name) === unit)
      .reduce((sum, r) => sum + (r.row_count || 0), 0);
    return acc;
  }, {} as Record<string, number>);

  const overallTotal = Object.keys(totalRowCounts).reduce(
    (sum, key) => sum + (totalRowCounts[key] || 0),
    0,
  );

  return [...baseRows, { taskType: 'Total', counts: { ...totalRowCounts, Total: overallTotal } }];
};
